interface Interval {
  startDate: string;
  endDate: string;
}

interface Result {
  today: string;
  inverse: boolean;
  firstInterval: Interval;
  secondInterval: Interval;
}

const formatDate = (date?: Date) => date?.toLocaleDateString('pt-BR') ?? '-';

const getDynamicDateRange = <T extends string>(format: T, N: number): Result => {
  const today = new Date();
  const inverse = format.startsWith('not');
  let startDate;
  let endDate;
  let secondStartDate;
  const secondEndDate = new Date(9999, 11, 31);
  let startQuarter;
  let startSemester;
  let startWeek;

  switch (format) {
    case 'previousY':
    case 'previousyear':
      startDate = new Date(today.getFullYear() - N, 0, 1);
      endDate = new Date(today.getFullYear() - 1, 11, 31);
      break;

    case 'not-previousY':
    case 'not-previousyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear() - N - 1, 11, 31);
      secondStartDate = new Date(today.getFullYear(), 0, 1);
      break;

    case 'previousM':
      startDate = new Date(today.getFullYear(), today.getMonth() - N, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;

    case 'not-previousM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;

    case 'previousD':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - N);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-previousD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - N - 1);
      secondStartDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      break;

    case 'previousQ':
      startQuarter = today.getMonth() - (today.getMonth() % 3) - 3 * N;
      startDate = new Date(today.getFullYear(), startQuarter, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3), 0);
      break;

    case 'not-previousQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3) - 3 * N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3), 1);
      break;

    case 'previousS':
      startSemester = today.getMonth() < 6 ? 0 : 6;
      startDate = new Date(today.getFullYear(), startSemester - 6 * N, 1);
      endDate = new Date(today.getFullYear(), startSemester, 0);
      break;

    case 'not-previousS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? -6 * N : 6 - 6 * N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 0 : 6, 1);
      break;

    case 'previousW':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() - 7 * N);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 1);
      break;

    case 'not-previousW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7 * N - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() - today.getDay());
      break;

    case 'current_and_previousY':
    case 'current_and_previousyear':
      startDate = new Date(today.getFullYear() - N, 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31);
      break;

    case 'not-current_and_previousY':
    case 'not-current_and_previousyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear() - N - 1, 11, 31);
      secondStartDate = new Date(today.getFullYear() + 1, 0, 1);
      break;

    case 'current_and_previousM':
      startDate = new Date(today.getFullYear(), today.getMonth() - N, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;

    case 'not-current_and_previousM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      break;

    case 'current_and_previousD':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - N);
      endDate = today;
      break;

    case 'not-current_and_previousD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - N - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() + 1);
      break;

    case 'current_and_previousQ':
      startQuarter = today.getMonth() - (today.getMonth() % 3) - 3 * N;
      startDate = new Date(today.getFullYear(), startQuarter, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 3 - (today.getMonth() % 3), 0);
      break;

    case 'not-current_and_previousQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3) - 3 * N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() + 3 - (today.getMonth() % 3), 1);
      break;

    case 'current_and_previousS':
      startSemester = today.getMonth() < 6 ? 0 : 6;
      startDate = new Date(today.getFullYear(), startSemester - 6 * N, 1);
      endDate = new Date(today.getFullYear(), startSemester + 6, 0);
      break;

    case 'not-current_and_previousS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? -6 * N : 6 - 6 * N, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 6 : 12, 1);
      break;

    case 'current_and_previousW':
      startWeek = new Date(today);
      startWeek.setDate(today.getDate() - today.getDay());
      startDate = new Date(startWeek);
      startDate.setDate(startWeek.getDate() - N * 7);
      endDate = new Date(startWeek);
      endDate.setDate(startWeek.getDate() + 6);
      break;

    case 'not-current_and_previousW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7 * N - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() + (7 - today.getDay()));
      break;

    case 'followingY':
    case 'followingyear':
      startDate = new Date(today.getFullYear() + 1, 0, 1);
      endDate = new Date(today.getFullYear() + N + 1, 0, 0);
      break;

    case 'not-followingY':
    case 'not-followingyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31);
      secondStartDate = new Date(today.getFullYear() + N + 1, 0, 1);
      break;

    case 'followingM':
      startDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + N + 1, 0);
      break;

    case 'not-followingM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() + N + 1, 1);
      break;

    case 'followingD':
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() + N);
      break;

    case 'not-followingD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      secondStartDate = new Date(today);
      secondStartDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + N + 1);
      break;

    case 'followingQ':
      startDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3) + 3, 1);
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() - (today.getMonth() % 3) + 3 * (N + 1),
        0,
      );
      break;

    case 'not-followingQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - (today.getMonth() % 3) + 3, 0);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() - (today.getMonth() % 3) + 3 * (N + 1),
        1,
      );
      break;

    case 'followingS':
      startDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 6 : 12, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 6 + 6 * N : 12 + 6 * N, 0);
      break;

    case 'not-followingS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 6 : 12, 0);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() < 6 ? 6 + 6 * N : 12 + 6 * N,
        1,
      );
      break;

    case 'followingW':
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 7 - today.getDay());
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 7 * (N + 1) - today.getDay() - 1);
      break;

    case 'not-followingW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 7 - today.getDay() - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() + 7 * (N + 1) - today.getDay());
      break;

    case 'current_and_followingY':
    case 'current_and_followingyear':
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = new Date(today.getFullYear() + N + 1, 0, 0);
      break;

    case 'not-current_and_followingY':
    case 'not-current_and_followingyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), 0, 0);
      secondStartDate = new Date(today.getFullYear() + N + 1, 0, 1);
      break;

    case 'current_and_followingM':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + N + 1, 0);
      break;

    case 'not-current_and_followingM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() + N + 1, 1);
      break;

    case 'current_and_followingD':
      startDate = new Date(today);
      startDate.setDate(today.getDate());
      endDate = new Date(today);
      endDate.setDate(today.getDate() + N);
      break;

    case 'not-current_and_followingD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      secondStartDate = new Date(today);
      secondStartDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + N + 1);
      break;

    case 'current_and_followingQ':
      startDate = new Date(today.getFullYear(), today.getMonth() - today.getMonth() % 3, 1);
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() - (today.getMonth() % 3) + 3 * (N + 1),
        0,
      );
      break;

    case 'not-current_and_followingQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - today.getMonth() % 3, 0);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() - (today.getMonth() % 3) + 3 * (N + 1),
        1,
      );
      break;

    case 'current_and_followingS':
      startDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 0 : 6, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 6 + 6 * N : 12 + 6 * N, 0);
      break;

    case 'not-current_and_followingS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() < 6 ? 0 : 6, 0);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() < 6 ? 6 + 6 * N : 12 + 6 * N,
        1,
      );
      break;

    case 'current_and_followingW':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 7 * (N + 1) - today.getDay() - 1);
      break;

    case 'not-current_and_followingW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() + 7 * (N + 1) - today.getDay());
      break;

    case 'lastY':
    case 'lastyear':
      startDate = new Date(today.getFullYear() - N + 1, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastY':
    case 'not-lastyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear() - N + 1, 0, 0);
      secondStartDate = today;
      break;

    case 'lastM':
      startDate = new Date(today.getFullYear(), today.getMonth() - N + 1, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() - N + 1, 0);
      secondStartDate = today;
      break;

    case 'lastD':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - N);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - N - 1);
      secondStartDate = today;
      break;

    case 'lastQ':
      startQuarter = today.getMonth() - (today.getMonth() % 3) - 3 * (N - 1);
      startDate = new Date(today.getFullYear(), startQuarter, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() - (today.getMonth() % 3) - 3 * (N - 1),
        0,
      );
      secondStartDate = today;
      break;

    case 'lastS':
      startSemester = today.getMonth() < 6 ? 0 : 6;
      startDate = new Date(today.getFullYear(), startSemester - 6 * (N - 1), 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() < 6 ? -6 * (N - 1) : 6 - 6 * (N - 1),
        0,
      );
      secondStartDate = today;
      break;

    case 'lastW':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() - 7 * (N - 1));
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;

    case 'not-lastW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7 * (N - 1) - 1);
      secondStartDate = today;
      break;

    case 'nextY':
    case 'nextyear':
      startDate = today;
      endDate = new Date(today.getFullYear() + N, 0, 0);
      break;

    case 'not-nextY':
    case 'not-nextyear':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      secondStartDate = new Date(today.getFullYear() + N, 0, 1);
      break;

    case 'nextM':
      startDate = today;
      endDate = new Date(today.getFullYear(), today.getMonth() + N, 0);
      break;

    case 'not-nextM':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      secondStartDate = new Date(today.getFullYear(), today.getMonth() + N, 1);
      break;

    case 'nextD':
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + N - 1);
      break;

    case 'not-nextD':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(secondStartDate.getDate() + N);
      break;

    case 'nextQ':
      startDate = today;
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() + (today.getMonth() % 3) + 3 * N,
        0,
      );
      break;

    case 'not-nextQ':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() + (today.getMonth() % 3) + 3 * N,
        1,
      );
      break;

    case 'nextS':
      startDate = today;
      endDate = new Date(
        today.getFullYear(),
        today.getMonth() < 6 ? 6 + 6 * (N - 1) : 12 + 6 * (N - 1),
        0,
      );
      break;

    case 'not-nextS':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      secondStartDate = new Date(
        today.getFullYear(),
        today.getMonth() < 6 ? 6 + 6 * (N - 1) : 12 + 6 * (N - 1),
        1,
      );
      break;

    case 'nextW':
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 7 * N - today.getDay() - 1);
      break;

    case 'not-nextW':
      startDate = new Date(0, 0, 1);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      secondStartDate = new Date(today);
      secondStartDate.setDate(today.getDate() + 7 * N - today.getDay());
      break;

    default:
      return {
        today: 'Filtro não reconhecido',
        inverse: false,
        firstInterval: {
          startDate: 'Filtro não reconhecido',
          endDate: 'Filtro não reconhecido',
        },
        secondInterval: {
          startDate: 'Filtro não reconhecido',
          endDate: 'Filtro não reconhecido',
        },
      };
  }

  const result: Result = {
    today: formatDate(today),
    inverse,
    firstInterval: {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    },
    secondInterval: {
      startDate: '-',
      endDate: '-',
    },
  };

  if (inverse) {
    result.secondInterval = {
      startDate: formatDate(secondStartDate),
      endDate: formatDate(secondEndDate),
    };
  }

  return result;
};

export default getDynamicDateRange;
